import * as mapboxgl from 'mapbox-gl';
import { ApMapboxStyler } from './ApMapboxStyler.ts';
import { TrackingService } from '../../support/tracking/TrackingService.ts';
import { State } from '@autopylot-internal/state';
import { ISmartStorageProviderV2, StorageProvider } from '../../support/StorageProvider.ts';
import { EnvConfiguration } from '../../app/config/EnvConfiguration.ts';

export type IMapboxStyleId = 'streets-v12' | 'satellite-streets-v12' | 'outdoors-v12';
// 'light-v11',
// 'dark-v11',
// 'satellite-v9',
// 'navigation-day-v1',
// 'navigation-night-v1',

class BaseMapStorage {
    static inject = () => [StorageProvider];
    constructor(storage: StorageProvider){
        this.activeStyleId = storage.getStringProvider('basemap.styleId', { storageType: 'localStorage' });
    }
    activeStyleId: ISmartStorageProviderV2<string>;
}

export class BaseMapState extends State<{}> {
    static inject = () => [
        ApMapboxStyler,
        TrackingService,
        BaseMapStorage,
        EnvConfiguration
    ];

    constructor(
        private styler: ApMapboxStyler,
        private tracker: TrackingService,
        private storageV2: BaseMapStorage,
        private config: EnvConfiguration
    ){
        super({});
        this.activeStyleId = this.storageV2.activeStyleId.exists() ? this.storageV2.activeStyleId.get() as IMapboxStyleId : 'streets-v12';
    }

    map!: mapboxgl.Map;

    private activeStyleId: IMapboxStyleId;

    getCurrentBaseMapUrl = (): string => {
        return `mapbox://styles/mapbox/${this.activeStyleId}`;
    };

    switchBaseMap = async (styleId: IMapboxStyleId): Promise<void> => {
        // inspired by https://github.com/mapbox/mapbox-gl-js/issues/4006#issuecomment-772462907
        // if we simply call .setStyle()
        // then our sources and layers are lost
        // so this function aims to reconcile the new style with the sources and layers we've added
        const fromBaseMap = this.activeStyleId;
        this.activeStyleId = styleId;

        this.tracker.track('Base Map Selected', () => ({
            fromBaseMap,
            toBaseMap: styleId
        }));

        const newStyle = await fetch(`https://api.mapbox.com/styles/v1/mapbox/${styleId}?access_token=${this.config.MAPBOX_TOKEN}`).then(res => res.json());
        newStyle.sources = {
            ...this.styler.getSources(this.map),
            ...newStyle.sources
        };

        newStyle.layers = [
            ...newStyle.layers,
            ...this.styler.getLayers(this.map)
        ];

        this.map.setStyle(newStyle, { diff: true, localFontFamily: undefined, localIdeographFontFamily: undefined });
        this.storageV2.activeStyleId.set(styleId);
        this.setState({ });
    };

    getCurrentStyleId = (): IMapboxStyleId => {
        return this.activeStyleId;
    };
}
